<template>
  <header class="app-header">
    <!-- 左：多页面标签 -->
    <div class="header-left">
      <nav class="tab-list" aria-label="已打开页面">
        <button
          v-for="(tab, index) in tabs"
          :key="tab.path"
          type="button"
          class="workspace-tab"
          :class="{ active: tab.path === $route.path }"
          @click="$emit('activate-tab', tab)"
        >
          <span>{{ tab.title }}</span>
          <i v-if="tabs.length > 1" class="el-icon-close tab-close" @click.stop="$emit('close-tab', index)" />
        </button>
      </nav>
      <el-tooltip content="关闭其他页面" placement="bottom">
        <button v-if="tabs.length > 1" class="tabs-action" type="button" @click="$emit('close-other-tabs')">
          <i class="el-icon-close" />
        </button>
      </el-tooltip>
    </div>

    <!-- 右：主题切换 + 窗口控制 -->
    <div class="header-right">
      <div class="theme-switch">
        <i class="el-icon-sunny sun" :class="{ dim: isDark }" />
        <el-switch
          v-model="isDark"
          :width="40"
          active-color="#5a5fe0"
          @change="onThemeChange"
        />
        <i class="el-icon-moon moon" :class="{ dim: !isDark }" />
      </div>

      <!-- 窗口控制：最小化 / 最大化 / 关闭 -->
      <div class="win-controls">
        <button class="win-btn" title="最小化" @click="handleWin('minimize')">
          <i class="el-icon-minus" />
        </button>
        <button class="win-btn" title="最大化/还原" @click="handleWin('maximize')">
          <i class="el-icon-full-screen" />
        </button>
        <button class="win-btn win-close" title="关闭" @click="handleWin('close')">
          <i class="el-icon-close" />
        </button>
      </div>
    </div>
  </header>
</template>

<script>
import { getTheme, setTheme } from '@/utils/theme'

export default {
  name: 'AppHeader',
  props: { tabs: { type: Array, default: () => [] } },
  data() {
    return {
      isDark: getTheme() === 'dark'
    }
  },
  methods: {
    onThemeChange(val) {
      setTheme(val ? 'dark' : 'light')
      this.$message({
        message: val ? '已切换至深色模式' : '已切换至浅色模式',
        type: 'success',
        duration: 1200
      })
    },
    // 窗口控制：桌面客户端（Electron）环境走 IPC；浏览器环境仅关闭生效
    handleWin(action) {
      const ipc = window.electron && window.electron.ipcRenderer
      if (ipc) {
        ipc.send('window-control', action)
        return
      }
      if (action === 'close') {
        window.close()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.app-header {
  position: relative;
  box-sizing: border-box;
  height: $header-height;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 16px;
  background: var(--bg-header);
  border-bottom: 1px solid var(--border-hairline);
}

.header-left {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
}

.tab-list {
  display:flex;
  align-items:center;
  gap:5px;
  min-width:0;
  flex:1;
  overflow-x:auto;
  overflow-y:hidden;
  scrollbar-width:thin;
  scrollbar-color: rgba(132, 144, 165, .42) transparent;
  &::-webkit-scrollbar { height:4px; }
  &::-webkit-scrollbar-track { background:transparent; }
  &::-webkit-scrollbar-thumb { border-radius:999px; background:rgba(132, 144, 165, .42); }
  &::-webkit-scrollbar-thumb:hover { background:var(--brand); }
}
.workspace-tab { display:flex; align-items:center; flex:0 0 auto; gap:8px; min-width:112px; max-width:210px; height:36px; padding:0 10px; border:1px solid transparent; border-radius:7px; background:transparent; color:var(--text-secondary); font:inherit; font-size:12px; cursor:pointer; white-space:nowrap; transition:background .15s ease,color .15s ease,border-color .15s ease; }
.workspace-tab span { overflow:hidden; text-overflow:ellipsis; }
.workspace-tab:hover { color:var(--text-primary); background:var(--bg-hover); }
.workspace-tab.active { color:var(--brand); background:var(--bg-page); border-color:var(--border-hairline); font-weight:600; }
.tab-close { display:grid; place-items:center; flex-shrink:0; width:18px; height:18px; margin-left:auto; border-radius:50%; color:var(--text-secondary); font-size:11px; }
.tab-close:hover { color:var(--text-primary); background:var(--border-hairline); }
.tabs-action { width:28px; height:30px; flex-shrink:0; margin:0 4px; border:0; border-radius:6px; background:transparent; color:var(--text-secondary); cursor:pointer; }
.tabs-action:hover { color:var(--brand); background:var(--bg-hover); }

.header-right {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 0 0 auto;

  .theme-switch {
    display: flex;
    align-items: center;
    gap: 8px;

    .sun,
    .moon {
      font-size: 16px;
      color: var(--warning);
      transition: opacity 0.2s ease, color 0.2s ease;

      &.dim {
        opacity: 0.35;
      }
    }
    .moon {
      color: var(--brand);
    }
  }

  // 窗口控制按钮（桌面客户端风格）
  .win-controls {
    display: flex;
    align-items: center;
    margin-right: -16px; // 抵消顶栏右内边距，按钮贴边

    .win-btn {
      width: 44px;
      height: $header-height;
      border: none;
      background: transparent;
      color: var(--text-regular);
      font-size: 14px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s ease, color 0.15s ease;
      outline: none;

      &:hover {
        background: var(--bg-hover);
        color: var(--text-primary);
      }

      // 关闭键悬停红色（对照桌面客户端惯例）
      &.win-close:hover {
        background: var(--danger);
        color: #ffffff;
      }
    }
  }
}

@media (max-width: 820px) {
  .app-header { gap:8px; padding:0 8px; }
  .header-right { gap:6px; }
  .workspace-tab { min-width:96px; padding:0 7px; }
  .win-controls .win-btn { width:34px; }
}

@media (max-width: 700px) {
  .app-header { gap:6px; }
  .header-right { gap:8px; }
  .header-right .win-controls { display:none; }
  .header-right .theme-switch { gap:5px; }
  .header-right .theme-switch .sun,
  .header-right .theme-switch .moon { font-size:14px; }
  .tabs-action { margin:0 1px; }
}

@media (max-width: 420px) {
  .app-header { padding:0 6px; }
  .workspace-tab { min-width:84px; height:34px; gap:5px; padding:0 6px; }
  .tab-close { width:16px; height:16px; }
  .tabs-action { width:24px; }
}
</style>
